/**
 * eSIM Profile Inventory Service
 *
 * Frontend service for managing eSIM profiles, groups, and networks.
 * Uses Firebase client SDK directly following flowService.ts pattern.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  increment,
  Timestamp,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { canUseSharedGroup, DEFAULT_SHARED_ESIM_GROUP_ID, DEFAULT_SHARED_ESIM_GROUP_NAME } from '@simcheck/shared-types';
import { createOrgScopedQuery, validateQueryResults } from '../lib/firestore-helpers';
import jsQR from 'jsqr';
import Papa from 'papaparse';
import type {
  Network,
  ESimGroup,
  ESimProfile,
  ESimImportResult,
  ESimInventoryStats,
  DownloadLimitType,
  ESimProfileStatus,
} from '@simcheck/shared-types';

// ============================================================================
// HELPERS
// ============================================================================

// Helper to convert Firestore Timestamp or ISO string to Date
function toDate(value: any): Date | undefined {
  if (!value) return undefined;
  if (value.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  if (value instanceof Date) {
    return value;
  }
  return undefined;
}

// ============================================================================
// NETWORKS
// ============================================================================

/**
 * Get all global networks (admin-managed, available to everyone)
 */
export async function getGlobalNetworks(): Promise<Network[]> {
  const q = query(
    collection(db, 'networks'),
    where('isGlobal', '==', true),
    where('isActive', '==', true),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: toDate(doc.data().createdAt) || new Date(),
    updatedAt: toDate(doc.data().updatedAt) || new Date(),
  })) as Network[];
}

/**
 * Get networks available to an organization (global + custom)
 */
export async function getNetworksForOrg(organizationId: string): Promise<Network[]> {
  const globalNetworks = await getGlobalNetworks();

  // Get custom networks for this org
  const customQuery = query(
    collection(db, 'networks'),
    where('isGlobal', '==', false),
    where('organizationId', '==', organizationId),
    where('isActive', '==', true),
    orderBy('name', 'asc')
  );

  const customSnapshot = await getDocs(customQuery);
  const customNetworks = customSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: toDate(doc.data().createdAt) || new Date(),
    updatedAt: toDate(doc.data().updatedAt) || new Date(),
  })) as Network[];

  return [...globalNetworks, ...customNetworks];
}

/**
 * Module-level cache of global networks. The MCC-MNC catalogue seeds ~2.5k
 * global `networks` docs, so we load them once per session and reuse across
 * combobox opens / batch imports instead of re-reading thousands of docs.
 * Globals are org-independent; org-specific custom networks are fetched fresh.
 */
let globalNetworksCache: Promise<Network[]> | null = null;

function loadGlobalNetworksCached(force = false): Promise<Network[]> {
  if (force || !globalNetworksCache) {
    globalNetworksCache = getGlobalNetworks().catch((err) => {
      // Don't poison the cache on failure — allow a retry on next call.
      globalNetworksCache = null;
      throw err;
    });
  }
  return globalNetworksCache;
}

/**
 * Load the full network catalogue available to an org: every global network
 * (cached) plus the org's custom networks. Used by the searchable Network
 * combobox and by batch-import name resolution. Pass `force` to bypass the
 * global cache (e.g. after seeding new networks).
 */
export async function loadNetworkCatalog(
  organizationId: string,
  options: { force?: boolean } = {}
): Promise<Network[]> {
  const globalNetworks = await loadGlobalNetworksCached(options.force);

  const customQuery = query(
    collection(db, 'networks'),
    where('isGlobal', '==', false),
    where('organizationId', '==', organizationId),
    where('isActive', '==', true),
    orderBy('name', 'asc')
  );
  const customSnapshot = await getDocs(customQuery);
  const customNetworks = customSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: toDate(doc.data().createdAt) || new Date(),
    updatedAt: toDate(doc.data().updatedAt) || new Date(),
  })) as Network[];

  // Custom networks first so org-specific entries surface above the catalogue.
  return [...customNetworks, ...globalNetworks];
}

/**
 * Create a network manually.
 *
 * Scope is controlled by `data.isGlobal`:
 *   - `false` (default) — a custom network scoped to `organizationId`, visible
 *     only to that org (org owners/admins). Surfaces under "Your networks".
 *   - `true` — added to the shared MCC-MNC catalogue, visible to every org
 *     (super admins only; the `networks` create rule gates this server-side).
 *
 * Used by NetworkCombobox's "Add a network manually" affordance to fill a
 * catalogue gap (an MCC/MNC that isn't seeded).
 */
export async function createCustomNetwork(
  organizationId: string,
  userId: string,
  data: {
    name: string;
    mcc: string;
    mnc: string;
    country: string;
    countryCode: string;
    logo?: string;
    isGlobal?: boolean;
  }
): Promise<Network> {
  const { isGlobal = false, ...fields } = data;

  const networkData: Record<string, any> = {
    ...fields,
    isGlobal,
    // A global network belongs to the shared catalogue and has no owning org.
    organizationId: isGlobal ? null : organizationId,
    isActive: true,
    source: 'custom',
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'networks'), networkData);

  // The session-level global cache won't include a freshly-added global
  // network — drop it so the next catalogue load re-reads it.
  if (isGlobal) globalNetworksCache = null;

  return {
    id: docRef.id,
    ...fields,
    isGlobal,
    organizationId: isGlobal ? undefined : organizationId,
    isActive: true,
    source: 'custom',
    createdBy: userId,
    createdAt: new Date(),
    updatedAt: new Date(),
  } as Network;
}

// ============================================================================
// GROUPS
// ============================================================================

/**
 * Get all groups for an organization with accurate profile counts
 */
export async function getGroupsForOrg(organizationId: string): Promise<ESimGroup[]> {
  const q = createOrgScopedQuery('esim_groups', organizationId, [orderBy('name', 'asc')]);
  const snapshot = await getDocs(q);

  const groups = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: toDate(doc.data().createdAt) || new Date(),
    updatedAt: toDate(doc.data().updatedAt) || new Date(),
  })) as ESimGroup[];

  if (import.meta.env.DEV) {
    validateQueryResults(groups, organizationId, 'esim_groups');
  }

  // Calculate actual profile counts for each group
  if (groups.length > 0) {
    const profilesQuery = createOrgScopedQuery('esim_profiles', organizationId, []);
    const profilesSnapshot = await getDocs(profilesQuery);

    // Count profiles per group
    const groupCounts: Record<string, number> = {};
    profilesSnapshot.docs.forEach(doc => {
      const groupId = doc.data().groupId;
      if (groupId) {
        groupCounts[groupId] = (groupCounts[groupId] || 0) + 1;
      }
    });

    // Update groups with accurate counts
    groups.forEach(group => {
      group.profileCount = groupCounts[group.id] || 0;
    });
  }

  return groups;
}

/**
 * Get a single group by ID
 */
export async function getGroup(groupId: string, organizationId: string): Promise<ESimGroup | null> {
  const docRef = doc(db, 'esim_groups', groupId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const group = {
    id: docSnap.id,
    ...docSnap.data(),
    createdAt: toDate(docSnap.data().createdAt) || new Date(),
    updatedAt: toDate(docSnap.data().updatedAt) || new Date(),
  } as ESimGroup;

  // Verify tenant isolation
  if (group.organizationId !== organizationId) {
    throw new Error('Unauthorized access to group');
  }

  return group;
}

/**
 * Create a new group
 */
export async function createGroup(
  organizationId: string,
  userId: string,
  data: { name: string; description?: string; color?: string; apn?: string }
): Promise<ESimGroup> {
  const groupData = {
    name: data.name,
    description: data.description || null,
    color: data.color || null,
    // Group default data-plane APN (issue #161). Blank → null so the
    // execution-time resolver falls through to the network default.
    apn: (data.apn || '').trim() || null,
    organizationId,
    profileCount: 0,
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'esim_groups'), groupData);

  return {
    id: docRef.id,
    name: data.name,
    description: data.description,
    color: data.color,
    apn: (data.apn || '').trim() || undefined,
    organizationId,
    profileCount: 0,
    createdBy: userId,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Update a group
 */
export async function updateGroup(
  groupId: string,
  organizationId: string,
  updates: { name?: string; description?: string; color?: string; apn?: string }
): Promise<void> {
  // Verify access
  const existing = await getGroup(groupId, organizationId);
  if (!existing) {
    throw new Error('Group not found');
  }

  // Build the update explicitly — Firestore updateDoc rejects `undefined`
  // values, so spreading the partial `updates` object verbatim breaks as
  // soon as a caller passes e.g. `description: undefined`. Blank description
  // and APN clear to null (matching create).
  const updateData: Record<string, any> = {};
  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.description !== undefined) updateData.description = (updates.description || '').trim() || null;
  if (updates.color !== undefined) updateData.color = updates.color;
  if (updates.apn !== undefined) updateData.apn = (updates.apn || '').trim() || null;

  const docRef = doc(db, 'esim_groups', groupId);
  await updateDoc(docRef, {
    ...updateData,
    updatedAt: serverTimestamp(),
  });

  // Renames must propagate to the members' denormalized `groupName` (the
  // table/badges read it) — otherwise every member keeps showing the old
  // name until it's individually touched. Chunked under the 500-write limit.
  if (updates.name !== undefined && updates.name !== existing.name) {
    const membersSnap = await getDocs(query(
      collection(db, 'esim_profiles'),
      where('organizationId', '==', organizationId),
      where('groupId', '==', groupId)
    ));
    const CHUNK = 450;
    for (let i = 0; i < membersSnap.docs.length; i += CHUNK) {
      const batch = writeBatch(db);
      for (const member of membersSnap.docs.slice(i, i + CHUNK)) {
        batch.update(member.ref, { groupName: updates.name, updatedAt: serverTimestamp() });
      }
      await batch.commit();
    }
  }
}

/**
 * Merge one group into another (issue #160): move every member profile of
 * `sourceGroupId` to `targetGroupId`, fix both cached profileCounts, then
 * delete the (now empty) source group. Chunked writeBatch so large groups
 * stay under Firestore's 500-writes-per-batch limit. Returns the number of
 * profiles moved.
 */
export async function mergeGroup(
  sourceGroupId: string,
  targetGroupId: string,
  organizationId: string
): Promise<number> {
  if (sourceGroupId === targetGroupId) {
    throw new Error('Cannot merge a group into itself');
  }

  const source = await getGroup(sourceGroupId, organizationId);
  if (!source) throw new Error('Source group not found');
  const target = await getGroup(targetGroupId, organizationId);
  if (!target) throw new Error('Target group not found');

  const membersSnap = await getDocs(query(
    collection(db, 'esim_profiles'),
    where('organizationId', '==', organizationId),
    where('groupId', '==', sourceGroupId)
  ));

  const CHUNK = 450;
  const docs = membersSnap.docs;
  for (let i = 0; i < docs.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const member of docs.slice(i, i + CHUNK)) {
      batch.update(member.ref, {
        groupId: targetGroupId,
        groupName: target.name,
        updatedAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  // Counts + source deletion in one final batch. Target count uses increment
  // (not an absolute) so concurrent profile adds during the merge aren't
  // clobbered; source is deleted outright so its count no longer matters.
  const finalBatch = writeBatch(db);
  if (docs.length > 0) {
    finalBatch.update(doc(db, 'esim_groups', targetGroupId), {
      profileCount: increment(docs.length),
      updatedAt: serverTimestamp(),
    });
  }
  finalBatch.delete(doc(db, 'esim_groups', sourceGroupId));
  await finalBatch.commit();

  return docs.length;
}

/**
 * Delete a group
 */
export async function deleteGroup(groupId: string, organizationId: string): Promise<void> {
  // Verify access
  const existing = await getGroup(groupId, organizationId);
  if (!existing) {
    throw new Error('Group not found');
  }

  // Check if any profiles use this group
  const profilesQuery = query(
    collection(db, 'esim_profiles'),
    where('organizationId', '==', organizationId),
    where('groupId', '==', groupId)
  );
  const profilesSnap = await getDocs(profilesQuery);

  if (!profilesSnap.empty) {
    throw new Error('Cannot delete group that has profiles assigned to it');
  }

  const docRef = doc(db, 'esim_groups', groupId);
  await deleteDoc(docRef);
}

// ============================================================================
// PROFILES
// ============================================================================

/**
 * Get all profiles for an organization
 */
export async function getProfilesForOrg(organizationId: string): Promise<ESimProfile[]> {
  const q = createOrgScopedQuery('esim_profiles', organizationId, [orderBy('createdAt', 'desc')]);
  const snapshot = await getDocs(q);

  const profiles = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    tags: doc.data().tags || [],
    createdAt: toDate(doc.data().createdAt) || new Date(),
    updatedAt: toDate(doc.data().updatedAt) || new Date(),
    expiresAt: toDate(doc.data().expiresAt),
    lastDownloadedAt: toDate(doc.data().lastDownloadedAt),
  })) as ESimProfile[];

  if (import.meta.env.DEV) {
    validateQueryResults(profiles, organizationId, 'esim_profiles');
  }

  return profiles;
}

/**
 * Get the super-admin **shared** eSIM inventory (profiles with `shared: true`,
 * `organizationId: null`). Not org-scoped — shared profiles belong to no tenant.
 * Firestore rules allow read/list to any authenticated user; visibility is a
 * UI/allocator concern (only super-admin-global listing + granted orgs' pickers
 * call this). Returns them newest-first.
 */
export async function getSharedProfiles(): Promise<ESimProfile[]> {
  const q = query(
    collection(db, 'esim_profiles'),
    where('shared', '==', true),
  );
  const snapshot = await getDocs(q);
  const profiles = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    tags: doc.data().tags || [],
    createdAt: toDate(doc.data().createdAt) || new Date(),
    updatedAt: toDate(doc.data().updatedAt) || new Date(),
    expiresAt: toDate(doc.data().expiresAt),
    lastDownloadedAt: toDate(doc.data().lastDownloadedAt),
  })) as ESimProfile[];
  // Newest first (kept client-side to avoid needing a composite index).
  profiles.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
  return profiles;
}

/**
 * Derive the distinct shared eSIM **groups** an org may use, from a list of
 * shared profiles. Each group renders as one "SimCheck Shared — <name>" picker
 * entry (value `shared:${sharedPoolSentinel(groupId)}`). Filters to groups the
 * org is granted via `canUseSharedGroup` so a tenant only sees its groups.
 */
export function sharedGroupsForOrg(
  sharedProfiles: ESimProfile[],
  org: unknown,
): { groupId: string; groupName: string }[] {
  const seen = new Map<string, string>();
  for (const p of sharedProfiles) {
    const gid = p.sharedGroupId || 'monogoto';
    if (!canUseSharedGroup(org as any, gid)) continue;
    if (!seen.has(gid)) seen.set(gid, p.sharedGroupName || (gid === DEFAULT_SHARED_ESIM_GROUP_ID ? DEFAULT_SHARED_ESIM_GROUP_NAME : gid));
  }
  return Array.from(seen.entries())
    .map(([groupId, groupName]) => ({ groupId, groupName }))
    .sort((a, b) => a.groupName.localeCompare(b.groupName));
}

/**
 * Derive ALL distinct shared eSIM groups from a list of shared profiles,
 * ignoring any per-org grant. Used by the super-admin OrganizationsPage to
 * render the full set of grantable groups (each org then opts into a subset).
 */
export function allSharedGroups(
  sharedProfiles: ESimProfile[],
): { groupId: string; groupName: string }[] {
  const seen = new Map<string, string>();
  for (const p of sharedProfiles) {
    const gid = p.sharedGroupId || 'monogoto';
    if (!seen.has(gid)) seen.set(gid, p.sharedGroupName || (gid === DEFAULT_SHARED_ESIM_GROUP_ID ? DEFAULT_SHARED_ESIM_GROUP_NAME : gid));
  }
  return Array.from(seen.entries())
    .map(([groupId, groupName]) => ({ groupId, groupName }))
    .sort((a, b) => a.groupName.localeCompare(b.groupName));
}

/**
 * Get a single profile by ID
 */
export async function getProfile(profileId: string, organizationId: string): Promise<ESimProfile | null> {
  const docRef = doc(db, 'esim_profiles', profileId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  const profile = {
    id: docSnap.id,
    ...data,
    tags: data.tags || [],
    createdAt: toDate(data.createdAt) || new Date(),
    updatedAt: toDate(data.updatedAt) || new Date(),
    expiresAt: toDate(data.expiresAt),
    lastDownloadedAt: toDate(data.lastDownloadedAt),
  } as ESimProfile;

  // Verify tenant isolation. Shared-inventory profiles are exempt by design:
  // they carry `organizationId: null`, are readable by any authenticated user
  // per firestore.rules, and their real gate is the per-group grant the
  // allocator enforces at run time (`canUseSharedGroup`). Throwing here would
  // just blank the details panel for a SIM the user legitimately picked.
  if (!profile.shared && profile.organizationId !== organizationId) {
    throw new Error('Unauthorized access to profile');
  }

  return profile;
}

/**
 * Create a new profile
 */
export async function createProfile(
  organizationId: string,
  userId: string,
  data: {
    name: string;
    description?: string;
    lpaString: string;
    networkId: string;
    networkName?: string;
    apn?: string;
    downloadLimit: DownloadLimitType;
    maxDownloads?: number;
    groupId?: string;
    groupName?: string;
    tags?: string[];
    notes?: string;
    expiresAt?: Date;
    /** Customer-declared: this SIM cannot be downloaded again; keep it on the modem it lands on. */
    locked?: boolean;
  }
): Promise<ESimProfile> {
  // Validate LPA string
  if (!validateLpaString(data.lpaString)) {
    throw new Error('Invalid LPA string format');
  }

  const profileData = {
    name: data.name,
    description: data.description || null,
    lpaString: data.lpaString,
    networkId: data.networkId,
    networkName: data.networkName || null,
    // Per-profile data APN override (issue #117). Normalized to null when
    // blank so the execution-time resolver falls through to the network
    // default APN, then the platform default (`internet`).
    apn: (data.apn || '').trim() || null,
    downloadLimit: data.downloadLimit,
    maxDownloads: data.downloadLimit === 'limited' ? data.maxDownloads : null,
    downloadCount: 0,
    status: 'available' as ESimProfileStatus,
    organizationId,
    groupId: data.groupId || null,
    groupName: data.groupName || null,
    tags: data.tags || [],
    notes: data.notes || null,
    expiresAt: data.expiresAt ? Timestamp.fromDate(data.expiresAt) : null,
    // Lock (display / pre-flight only today): see getESimLockInfo in shared-types.
    locked: data.locked === true,
    lockedAt: data.locked === true ? serverTimestamp() : null,
    lockedBy: data.locked === true ? userId : null,
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastDownloadedAt: null,
  };

  const docRef = await addDoc(collection(db, 'esim_profiles'), profileData);

  // Update group profile count if assigned
  if (data.groupId) {
    const groupRef = doc(db, 'esim_groups', data.groupId);
    await updateDoc(groupRef, {
      profileCount: increment(1),
      updatedAt: serverTimestamp(),
    });
  }

  return {
    id: docRef.id,
    ...data,
    organizationId,
    downloadCount: 0,
    status: 'available',
    tags: data.tags || [],
    createdBy: userId,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Update a profile
 */
export async function updateProfile(
  profileId: string,
  organizationId: string,
  updates: {
    name?: string;
    description?: string;
    networkId?: string;
    networkName?: string;
    apn?: string;
    msisdn?: string;
    downloadLimit?: DownloadLimitType;
    /** `null` clears the cap — pass it when leaving the `limited` mode. */
    maxDownloads?: number | null;
    groupId?: string;
    groupName?: string;
    tags?: string[];
    notes?: string;
    status?: ESimProfileStatus;
    expiresAt?: Date | null;
    /** Lock / unlock the SIM to the modem it is installed on. */
    locked?: boolean;
    /** Who flipped the lock (stored as `lockedBy`). */
    lockedBy?: string | null;
  }
): Promise<void> {
  // Verify access
  const existing = await getProfile(profileId, organizationId);
  if (!existing) {
    throw new Error('Profile not found');
  }

  const updateData: any = {
    updatedAt: serverTimestamp(),
  };

  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.description !== undefined) updateData.description = updates.description;
  if (updates.networkId !== undefined) updateData.networkId = updates.networkId;
  if (updates.networkName !== undefined) updateData.networkName = updates.networkName;
  if (updates.apn !== undefined) updateData.apn = (updates.apn || '').trim() || null;
  if (updates.msisdn !== undefined) updateData.msisdn = (updates.msisdn || '').trim() || null;
  if (updates.downloadLimit !== undefined) updateData.downloadLimit = updates.downloadLimit;
  if (updates.maxDownloads !== undefined) updateData.maxDownloads = updates.maxDownloads ?? null;
  // '' means "clear the group" — store null (passing `undefined` from callers
  // used to skip the field entirely, silently no-oping "No group").
  if (updates.groupId !== undefined) updateData.groupId = updates.groupId || null;
  if (updates.groupName !== undefined) updateData.groupName = updates.groupName || null;
  if (updates.tags !== undefined) updateData.tags = updates.tags;
  if (updates.notes !== undefined) updateData.notes = updates.notes;
  if (updates.status !== undefined) updateData.status = updates.status;
  if (updates.locked !== undefined) {
    updateData.locked = updates.locked === true;
    updateData.lockedAt = updates.locked === true ? serverTimestamp() : null;
    updateData.lockedBy = updates.locked === true ? updates.lockedBy ?? null : null;
  }
  if (updates.expiresAt !== undefined) {
    updateData.expiresAt = updates.expiresAt ? Timestamp.fromDate(updates.expiresAt) : null;
  }

  const docRef = doc(db, 'esim_profiles', profileId);
  await updateDoc(docRef, updateData);

  // Handle group count updates if group changed
  if (updates.groupId !== undefined && updates.groupId !== existing.groupId) {
    const batch = writeBatch(db);

    if (existing.groupId) {
      const oldGroupRef = doc(db, 'esim_groups', existing.groupId);
      batch.update(oldGroupRef, {
        profileCount: increment(-1),
        updatedAt: serverTimestamp(),
      });
    }

    if (updates.groupId) {
      const newGroupRef = doc(db, 'esim_groups', updates.groupId);
      batch.update(newGroupRef, {
        profileCount: increment(1),
        updatedAt: serverTimestamp(),
      });
    }

    await batch.commit();
  }
}

/**
 * Delete a profile
 */
export async function deleteProfile(profileId: string, organizationId: string): Promise<void> {
  // Verify access
  const existing = await getProfile(profileId, organizationId);
  if (!existing) {
    throw new Error('Profile not found');
  }

  const docRef = doc(db, 'esim_profiles', profileId);
  await deleteDoc(docRef);

  // Decrement group profile count if assigned
  if (existing.groupId) {
    const groupRef = doc(db, 'esim_groups', existing.groupId);
    await updateDoc(groupRef, {
      profileCount: increment(-1),
      updatedAt: serverTimestamp(),
    });
  }
}

// ============================================================================
// BATCH IMPORT
// ============================================================================

/**
 * Parse CSV content and return profile data
 */
export function parseCSV(csvContent: string): Array<{
  name: string;
  description?: string;
  lpaString: string;
  network: string;
  downloadLimit: DownloadLimitType;
  maxDownloads?: number;
  tags: string[];
  group?: string;
}> {
  const result = Papa.parse(csvContent, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim().toLowerCase(),
  });

  if (result.errors.length > 0) {
    throw new Error(`CSV parsing error: ${result.errors[0].message}`);
  }

  return (result.data as any[]).map(row => ({
    name: row.name?.trim() || '',
    description: row.description?.trim() || undefined,
    lpaString: row.lpastring?.trim() || row['lpa_string']?.trim() || '',
    network: row.network?.trim() || '',
    downloadLimit: (row.downloadlimit?.trim() || row['download_limit']?.trim() || 'unlimited') as DownloadLimitType,
    maxDownloads: row.maxdownloads ? parseInt(row.maxdownloads, 10) : (row['max_downloads'] ? parseInt(row['max_downloads'], 10) : undefined),
    tags: row.tags ? row.tags.split('|').map((t: string) => t.trim()).filter(Boolean) : [],
    group: row.group?.trim() || undefined,
  }));
}

/**
 * Batch import profiles from parsed CSV data
 */
export async function batchImportProfiles(
  organizationId: string,
  userId: string,
  profiles: Array<{
    name: string;
    description?: string;
    lpaString: string;
    networkId: string;
    networkName?: string;
    downloadLimit: DownloadLimitType;
    maxDownloads?: number;
    groupId?: string;
    groupName?: string;
    tags?: string[];
  }>
): Promise<ESimImportResult> {
  const result: ESimImportResult = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (let i = 0; i < profiles.length; i++) {
    const profileData = profiles[i];
    const rowNumber = i + 2; // Account for header row

    try {
      await createProfile(organizationId, userId, profileData);
      result.success++;
    } catch (error: any) {
      result.failed++;
      result.errors.push({
        row: rowNumber,
        error: error.message,
        data: profileData,
      });
    }
  }

  return result;
}

// ============================================================================
// QR CODE PARSING
// ============================================================================

/**
 * Parse QR code from image data and extract LPA string
 */
export function parseQRCode(imageData: ImageData): string | null {
  const code = jsQR(imageData.data, imageData.width, imageData.height);

  if (!code) {
    return null;
  }

  // Validate it's an LPA string
  if (validateLpaString(code.data)) {
    return code.data;
  }

  return null;
}

/**
 * Parse QR code from an image file
 */
export async function parseQRCodeFromFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const lpaString = parseQRCode(imageData);
        if (lpaString) {
          resolve(lpaString);
        } else {
          reject(new Error('No valid LPA string found in QR code'));
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate LPA string format
 * Valid format: LPA:1$<smdp-address>$<activation-code>
 */
export function validateLpaString(lpaString: string): boolean {
  if (!lpaString) return false;
  const lpaRegex = /^LPA:1\$[^$]+\$[^$]+$/;
  return lpaRegex.test(lpaString);
}

// ============================================================================
// STATISTICS
// ============================================================================

/**
 * Calculate inventory statistics for an organization
 */
export async function getInventoryStats(organizationId: string): Promise<ESimInventoryStats> {
  const profiles = await getProfilesForOrg(organizationId);
  const groups = await getGroupsForOrg(organizationId);

  // Count distinct networks actually referenced by this org's profiles. The
  // global catalogue holds ~2.5k networks, so "available networks" is no longer
  // a meaningful dashboard figure — networks-in-use is the org-relevant number.
  const usedNetworkIds = new Set(
    profiles.filter((p) => p.networkId).map((p) => p.networkId)
  );

  const stats: ESimInventoryStats = {
    totalProfiles: profiles.length,
    availableProfiles: 0,
    inUseProfiles: 0,
    downloadedProfiles: 0,
    expiredProfiles: 0,
    disabledProfiles: 0,
    totalGroups: groups.length,
    totalNetworks: usedNetworkIds.size,
    profilesByNetwork: {},
    profilesByGroup: {},
  };

  for (const profile of profiles) {
    // Count by status
    switch (profile.status) {
      case 'available':
        stats.availableProfiles++;
        break;
      case 'in_use':
        stats.inUseProfiles++;
        break;
      case 'downloaded':
        stats.downloadedProfiles++;
        break;
      case 'expired':
        stats.expiredProfiles++;
        break;
      case 'disabled':
        stats.disabledProfiles++;
        break;
    }

    // Count by network
    if (profile.networkName) {
      stats.profilesByNetwork[profile.networkName] = (stats.profilesByNetwork[profile.networkName] || 0) + 1;
    }

    // Count by group
    if (profile.groupName) {
      stats.profilesByGroup[profile.groupName] = (stats.profilesByGroup[profile.groupName] || 0) + 1;
    }
  }

  return stats;
}

// ============================================================================
// CSV TEMPLATE
// ============================================================================

/**
 * Generate a CSV template for batch import
 */
export function generateCSVTemplate(): string {
  const headers = ['name', 'description', 'lpaString', 'network', 'downloadLimit', 'maxDownloads', 'tags', 'group'];
  const exampleRow = [
    'Profile 1',
    'Test profile',
    'LPA:1$smdp.example.com$ABC123',
    'AT&T',
    'single_use',
    '',
    'test|production',
    'Production'
  ];

  return [headers.join(','), exampleRow.join(',')].join('\n');
}

/**
 * Download CSV template
 */
export function downloadCSVTemplate(): void {
  const template = generateCSVTemplate();
  const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'esim_profiles_template.csv';
  link.click();
}
